// Beat-wise / Retailer-wise Sales report. Shows secondary orders in a date
// range, bucketed by beat or by retailer, with each bucket broken down across
// the order lifecycle status (placed / accepted / fulfilled / rejected) —
// count + qty (kg).
// Distributor portal: the distributor's own orders (/portal/sales-report).
// SE / ASM / ZSM app: the distributors of their mapping (/sales-report), with
// a distributor picker and an extra Distributor-wise tab.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Calendar } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { useAuth } from "@/lib/auth";

function isoDaysAgo(n) {
  return new Date(Date.now() + 5.5 * 3600 * 1000 - n * 86400000).toISOString().slice(0, 10);
}

const STATUS_CHIPS = [
  { key: "placed", label: "Placed", cls: "bg-neutral-100 text-neutral-700" },
  { key: "accepted", label: "Approved", cls: "bg-blue-50 text-blue-700" },
  { key: "partial_fulfilled", label: "Partial", cls: "bg-amber-50 text-amber-700" },
  { key: "fulfilled", label: "Fulfilled", cls: "bg-emerald-50 text-emerald-700" },
  { key: "rejected", label: "Rejected", cls: "bg-red-50 text-red-700" },
];

const PATHS = {
  beat: "/portal/reports/beat-wise-sales",
  retailer: "/portal/reports/retailer-wise-sales",
  distributor: "/portal/reports/distributor-wise-sales",
};

export default function PortalSalesReport() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isDistributor = user?.role_code === "DISTRIBUTOR";
  const [scope, setScope] = useState("beat"); // "beat" | "retailer" | "distributor"
  const [dateFrom, setDateFrom] = useState(() => isoDaysAgo(0));
  const [dateTo, setDateTo] = useState(() => isoDaysAgo(0));
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [distributorId, setDistributorId] = useState("");

  // Field users pick from the distributors of their mapping.
  useEffect(() => {
    if (isDistributor) return;
    api.get("/portal/reports/distributors")
       .then(({ data }) => setDistributors(data.items || []))
       .catch(() => setDistributors([]));
  }, [isDistributor]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = { date_from: dateFrom, date_to: dateTo, ...(distributorId ? { distributor_id: distributorId } : {}) };
    api.get(PATHS[scope] || PATHS.beat, { params })
       .then(({ data }) => { if (!cancelled) setRows(data.items || []); })
       .catch((e) => toast.error(formatApiError(e)))
       .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [scope, dateFrom, dateTo, distributorId]);

  const totalKg = rows.reduce((a, r) => a + (r.total_kg || 0), 0);
  const totalOrders = rows.reduce((a, r) => a + (r.total_count || 0), 0);
  const rowName = (r) => (scope === "beat" ? r.beat_name : scope === "retailer" ? r.retailer_name : r.distributor_name);
  const rowCode = (r) => (scope === "retailer" ? r.retailer_code : scope === "distributor" ? r.distributor_code : "");

  return (
    <div className="p-4 pb-24 space-y-4" data-testid="portal-sales-report-page">
      <button onClick={() => nav(-1)} className="text-sm text-neutral-500 flex items-center gap-1"
              data-testid="portal-sales-report-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-neutral-400">Reports</div>
        <h1 className="text-xl font-extrabold text-neutral-900">Sales report</h1>
      </div>

      <Tabs value={scope} onValueChange={setScope}>
        <TabsList className={`grid w-full ${isDistributor ? "grid-cols-2" : "grid-cols-3"}`}>
          <TabsTrigger value="beat" data-testid="portal-sales-report-tab-beat">{isDistributor ? "Beat-wise" : "Beat"}</TabsTrigger>
          <TabsTrigger value="retailer" data-testid="portal-sales-report-tab-retailer">{isDistributor ? "Retailer-wise" : "Retailer"}</TabsTrigger>
          {!isDistributor && (
            <TabsTrigger value="distributor" data-testid="portal-sales-report-tab-distributor">Distributor</TabsTrigger>
          )}
        </TabsList>
      </Tabs>

      {!isDistributor && distributors.length > 1 && (
        <SearchableSelect
          value={distributorId}
          onChange={(v) => setDistributorId(v || "")}
          options={distributors}
          placeholder="All distributors"
          testid="portal-sales-report-distributor"
        />
      )}

      <div className="flex items-center gap-2">
        <Calendar className="h-4 w-4 text-neutral-500 shrink-0" />
        <Input type="date" value={dateFrom} max={dateTo}
               onChange={(e) => setDateFrom(e.target.value)}
               data-testid="portal-sales-report-date-from"
               className="h-9 bg-white text-neutral-900" />
        <span className="text-neutral-400 text-xs">to</span>
        <Input type="date" value={dateTo} min={dateFrom} max={isoDaysAgo(0)}
               onChange={(e) => setDateTo(e.target.value)}
               data-testid="portal-sales-report-date-to"
               className="h-9 bg-white text-neutral-900" />
      </div>

      {loading && (
        <div className="py-10 text-center text-neutral-400">
          <Loader2 className="h-5 w-5 inline animate-spin" />
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="flex items-center justify-between text-xs text-neutral-500 px-1" data-testid="portal-sales-report-total">
          <span>{rows.length} {scope === "beat" ? "beats" : scope === "retailer" ? "retailers" : "distributors"}</span>
          <span className="font-semibold text-neutral-800">{totalOrders} orders · {totalKg.toFixed(1)} kg</span>
        </div>
      )}

      {!loading && rows.length === 0 && (
        <Card className="border-dashed border-neutral-300 rounded-2xl" data-testid="portal-sales-report-empty">
          <CardContent className="p-6 text-center text-sm text-neutral-500">
            No orders in this date range.
          </CardContent>
        </Card>
      )}

      {!loading && rows.map((r) => (
        <Card key={r.id || rowName(r)} className="border-neutral-200 rounded-2xl"
              data-testid={`portal-sales-report-row-${r.id || "unassigned"}`}>
          <CardContent className="p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-neutral-900 truncate">
                  {rowName(r)}
                </div>
                {rowCode(r) && (
                  <div className="text-[11px] text-neutral-500">{rowCode(r)}</div>
                )}
              </div>
              <div className="text-right shrink-0">
                <div className="text-sm font-bold text-neutral-900">{(r.total_kg || 0).toFixed(1)} kg</div>
                <div className="text-[11px] text-neutral-500">{r.total_count} orders</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {STATUS_CHIPS.map((s) => {
                const count = r[`${s.key}_count`] || 0;
                if (!count) return null;
                return (
                  <span key={s.key} className={`text-[10px] font-medium px-2 py-1 rounded-full ${s.cls}`}>
                    {s.label} {count} · {(r[`${s.key}_kg`] || 0).toFixed(1)} kg
                  </span>
                );
              })}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
